import React, { useMemo } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";

export const TELEPHONY_PROVIDERS = {
  D2C_CLOUD_PBX: "D2C_CLOUD_PBX",
  GRANDSTREAM_UCM: "GRANDSTREAM_UCM",
};

const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]";

export const emptyTelephonyConfigValues = {
  provider: TELEPHONY_PROVIDERS.D2C_CLOUD_PBX,
  d2cBaseUrl: "",
  d2cAccessId: "",
  d2cAccessKey: "",
  gsBaseUrl: "",
  gsUsername: "",
  gsPassword: "",
  gsRecordingBaseUrl: "",
  gsSipDomain: "",
  gsSipWsUrl: "",
  gsSipWssUrl: "",
  gsWebrtcEnabled: true,
  gsCdrSyncEnabled: false,
  gsCdrEndpoint: "",
  gsRecordingEndpoint: "",
  gsEventWebhookSecret: "",
  gsIceStunUrl: "",
  gsIceTurnUrl: "",
  gsIceTurnUsername: "",
  gsIceTurnPassword: "",
  gsSipUdpPort: 5060,
  gsSipTransport: "UDP",
};

export const mapTelephonyConfigResponse = (data) => ({
  values: {
    provider: data?.provider || TELEPHONY_PROVIDERS.D2C_CLOUD_PBX,
    d2cBaseUrl: data?.d2cBaseUrl || "",
    d2cAccessId: "",
    d2cAccessKey: "",
    gsBaseUrl: data?.gsBaseUrl || "",
    gsUsername: data?.gsUsername || "",
    gsPassword: "",
    gsRecordingBaseUrl: data?.gsRecordingBaseUrl || "",
    gsSipDomain: data?.gsSipDomain || "",
    gsSipWsUrl: data?.gsSipWsUrl || "",
    gsSipWssUrl: data?.gsSipWssUrl || "",
    gsWebrtcEnabled: data?.gsWebrtcEnabled !== false,
    gsCdrSyncEnabled: Boolean(data?.gsCdrSyncEnabled),
    gsCdrEndpoint: data?.gsCdrEndpoint || "",
    gsRecordingEndpoint: data?.gsRecordingEndpoint || "",
    gsEventWebhookSecret: "",
    gsIceStunUrl: data?.gsIceStunUrl || "",
    gsIceTurnUrl: data?.gsIceTurnUrl || "",
    gsIceTurnUsername: data?.gsIceTurnUsername || "",
    gsIceTurnPassword: "",
    gsSipUdpPort: data?.gsSipUdpPort > 0 ? data.gsSipUdpPort : 5060,
    gsSipTransport: data?.gsSipTransport || "UDP",
  },
  flags: {
    d2cAccessIdConfigured: Boolean(data?.d2cAccessIdConfigured),
    d2cAccessKeyConfigured: Boolean(data?.d2cAccessKeyConfigured),
    gsPasswordConfigured: Boolean(data?.gsPasswordConfigured),
    gsEventWebhookSecretConfigured: Boolean(data?.gsEventWebhookSecretConfigured),
    gsIceTurnPasswordConfigured: Boolean(data?.gsIceTurnPasswordConfigured),
  },
  organizationId: data?.organizationId ?? null,
});

const trimOrNull = (value) => {
  const trimmed = (value || "").trim();
  return trimmed.length ? trimmed : null;
};

export const buildTelephonyConfigPayload = (values) => {
  const payload = {
    provider: values.provider,
    gsWebrtcEnabled: Boolean(values.gsWebrtcEnabled),
    gsCdrSyncEnabled: Boolean(values.gsCdrSyncEnabled),
    gsSipUdpPort: Number(values.gsSipUdpPort) || 5060,
    gsSipTransport: (values.gsSipTransport || "UDP").trim(),
  };

  payload.d2cBaseUrl = trimOrNull(values.d2cBaseUrl);
  if (values.d2cAccessId.trim()) payload.d2cAccessId = values.d2cAccessId.trim();
  if (values.d2cAccessKey.trim()) payload.d2cAccessKey = values.d2cAccessKey.trim();

  payload.gsBaseUrl = trimOrNull(values.gsBaseUrl);
  payload.gsUsername = trimOrNull(values.gsUsername);
  if (values.gsPassword.trim()) payload.gsPassword = values.gsPassword.trim();
  payload.gsRecordingBaseUrl = trimOrNull(values.gsRecordingBaseUrl);
  payload.gsSipDomain = trimOrNull(values.gsSipDomain);
  payload.gsSipWsUrl = trimOrNull(values.gsSipWsUrl);
  payload.gsSipWssUrl = trimOrNull(values.gsSipWssUrl);
  payload.gsCdrEndpoint = trimOrNull(values.gsCdrEndpoint);
  payload.gsRecordingEndpoint = trimOrNull(values.gsRecordingEndpoint);
  if (values.gsEventWebhookSecret.trim()) {
    payload.gsEventWebhookSecret = values.gsEventWebhookSecret.trim();
  }
  payload.gsIceStunUrl = trimOrNull(values.gsIceStunUrl);
  payload.gsIceTurnUrl = trimOrNull(values.gsIceTurnUrl);
  payload.gsIceTurnUsername = trimOrNull(values.gsIceTurnUsername);
  if (values.gsIceTurnPassword.trim()) payload.gsIceTurnPassword = values.gsIceTurnPassword.trim();

  return payload;
};

export const buildTelephonyConfigSchema = (t, callingEnabled) =>
  Yup.object({
    provider: Yup.string().oneOf([
      TELEPHONY_PROVIDERS.D2C_CLOUD_PBX,
      TELEPHONY_PROVIDERS.GRANDSTREAM_UCM,
    ]),
    gsSipDomain: Yup.string().trim().when(["provider"], {
      is: (provider) => callingEnabled && provider === TELEPHONY_PROVIDERS.GRANDSTREAM_UCM,
      then: (field) => field.required(t("ccTelephonyGsSipDomainRequired")),
    }),
  });

const SecretHint = ({ configured, t }) =>
  configured ? (
    <p className="mt-1 text-xs text-slate-500">{t("ccTelephonySecretConfigured")}</p>
  ) : null;

const TelephonyConfigForm = ({
  initialValues,
  flags,
  callingEnabled = true,
  saving = false,
  onSubmit,
  t,
}) => {
  const schema = useMemo(
    () => buildTelephonyConfigSchema(t, callingEnabled),
    [t, callingEnabled]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      enableReinitialize
      onSubmit={(values, helpers) => onSubmit(values, helpers)}
    >
      {({ values, isSubmitting }) => (
        <Form className="space-y-6">
          <div>
            <label className={FILTER_LABEL_CLASS} htmlFor="telephony-provider">
              {t("ccTelephonyProvider")}
            </label>
            <Field
              as="select"
              id="telephony-provider"
              name="provider"
              className={FILTER_FIELD_CLASS}
            >
              <option value={TELEPHONY_PROVIDERS.D2C_CLOUD_PBX}>{t("ccTelephonyProviderD2c")}</option>
              <option value={TELEPHONY_PROVIDERS.GRANDSTREAM_UCM}>
                {t("ccTelephonyProviderGrandstream")}
              </option>
            </Field>
          </div>

          {values.provider === TELEPHONY_PROVIDERS.D2C_CLOUD_PBX ? (
            <section className="space-y-4 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
              <h3 className="text-sm font-semibold text-slate-800">{t("ccTelephonyD2cSection")}</h3>
              <p className="text-xs text-slate-500">{t("ccTelephonyD2cHint")}</p>
              <div>
                <label className={FILTER_LABEL_CLASS} htmlFor="d2c-base-url">
                  {t("ccTelephonyD2cBaseUrl")}
                </label>
                <Field id="d2c-base-url" name="d2cBaseUrl" className={FILTER_FIELD_CLASS} />
              </div>
              <div>
                <label className={FILTER_LABEL_CLASS} htmlFor="d2c-access-id">
                  {t("ccTelephonyD2cAccessId")}
                </label>
                <Field id="d2c-access-id" name="d2cAccessId" className={FILTER_FIELD_CLASS} />
                <SecretHint configured={flags.d2cAccessIdConfigured} t={t} />
              </div>
              <div>
                <label className={FILTER_LABEL_CLASS} htmlFor="d2c-access-key">
                  {t("ccTelephonyD2cAccessKey")}
                </label>
                <Field
                  id="d2c-access-key"
                  name="d2cAccessKey"
                  type="password"
                  autoComplete="new-password"
                  className={FILTER_FIELD_CLASS}
                />
                <SecretHint configured={flags.d2cAccessKeyConfigured} t={t} />
              </div>
            </section>
          ) : (
            <section className="space-y-4 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
              <h3 className="text-sm font-semibold text-slate-800">{t("ccTelephonyGrandstreamSection")}</h3>
              <p className="text-xs text-slate-500">{t("ccTelephonyGrandstreamDesktopHint")}</p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-base-url">
                    {t("ccTelephonyGsBaseUrl")}
                  </label>
                  <Field id="gs-base-url" name="gsBaseUrl" className={FILTER_FIELD_CLASS} />
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-username">
                    {t("ccTelephonyGsUsername")}
                  </label>
                  <Field id="gs-username" name="gsUsername" className={FILTER_FIELD_CLASS} />
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-password">
                    {t("ccTelephonyGsPassword")}
                  </label>
                  <Field
                    id="gs-password"
                    name="gsPassword"
                    type="password"
                    autoComplete="new-password"
                    className={FILTER_FIELD_CLASS}
                  />
                  <SecretHint configured={flags.gsPasswordConfigured} t={t} />
                </div>
                <div className="md:col-span-2">
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-recording-base">
                    {t("ccTelephonyGsRecordingBaseUrl")}
                  </label>
                  <Field id="gs-recording-base" name="gsRecordingBaseUrl" className={FILTER_FIELD_CLASS} />
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-sip-domain">
                    {t("ccTelephonyGsSipDomain")}
                  </label>
                  <Field id="gs-sip-domain" name="gsSipDomain" className={FILTER_FIELD_CLASS} />
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-sip-wss">
                    {t("ccTelephonyGsSipWssUrl")}
                  </label>
                  <Field id="gs-sip-wss" name="gsSipWssUrl" className={FILTER_FIELD_CLASS} />
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-sip-ws">
                    {t("ccTelephonyGsSipWsUrl")}
                  </label>
                  <Field id="gs-sip-ws" name="gsSipWsUrl" className={FILTER_FIELD_CLASS} />
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-cdr-endpoint">
                    {t("ccTelephonyGsCdrEndpoint")}
                  </label>
                  <Field id="gs-cdr-endpoint" name="gsCdrEndpoint" className={FILTER_FIELD_CLASS} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Field type="checkbox" name="gsCdrSyncEnabled" className={CHECKBOX_CLASS} />
                  <span className="text-sm text-slate-700">{t("ccTelephonyGsCdrSyncEnabled")}</span>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Field type="checkbox" name="gsWebrtcEnabled" className={CHECKBOX_CLASS} />
                  <span className="text-sm text-slate-700">{t("ccTelephonyGsWebrtcEnabled")}</span>
                </div>
                <div className="md:col-span-2">
                  <label className={FILTER_LABEL_CLASS} htmlFor="gs-webhook-secret">
                    {t("ccTelephonyGsWebhookSecret")}
                  </label>
                  <Field
                    id="gs-webhook-secret"
                    name="gsEventWebhookSecret"
                    type="password"
                    autoComplete="new-password"
                    className={FILTER_FIELD_CLASS}
                  />
                  <SecretHint configured={flags.gsEventWebhookSecretConfigured} t={t} />
                </div>
              </div>
            </section>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving || isSubmitting}
              className={LISTING_PRIMARY_BUTTON_CLASS}
            >
              {t("ccSave")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TelephonyConfigForm;
